import { Grid } from '@mui/material';
import { businessData as businessDataAtom } from 'atoms';
import FormHeading from 'common/FormHeading';
import FormModal from 'common/FormModal';
import FormStyle from 'common/FormStyle';
import StatusBadge from 'common/StatusBadge';
import StickyActionFooter from 'common/StickyActionFooter';
import { formatDate } from 'common/formatters';
import { useRecoilState } from 'recoil';
import React, { useEffect, useState } from 'react';
import Form from '../InvoicePopup/Partials/Form';
import { useAuth } from 'context/AuthContext';

const coerceNumberValue = (value) => {
    if (value === null || value === undefined) return 0;
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : 0;
    }
    if (typeof value === 'string') {
        if (!value.trim()) return 0;
        const cleaned = value.replace(/[^0-9.,-]/g, '').replace(/,/g, '');
        const parsed = Number(cleaned);
        return Number.isFinite(parsed) ? parsed : 0;
    }
    if (typeof value === 'object') {
        if ('amount' in value) return coerceNumberValue(value.amount);
        if ('price' in value) return coerceNumberValue(value.price);
        if ('value' in value) return coerceNumberValue(value.value);
    }
    return 0;
};

const parseServiceDataFlexible = (serviceDataLike) => {
    if (!serviceDataLike) return [];
    if (Array.isArray(serviceDataLike)) {
        return serviceDataLike.filter(Boolean);
    }
    if (typeof serviceDataLike === 'string') {
        try {
            const parsed = JSON.parse(serviceDataLike);
            return parseServiceDataFlexible(parsed);
        } catch (error) {
            return [];
        }
    }
    if (typeof serviceDataLike === 'object') {
        if (Array.isArray(serviceDataLike.services)) {
            return parseServiceDataFlexible(serviceDataLike.services);
        }
        if (Array.isArray(serviceDataLike.serviceData)) {
            return parseServiceDataFlexible(serviceDataLike.serviceData);
        }
        if (Array.isArray(serviceDataLike.items)) {
            return parseServiceDataFlexible(serviceDataLike.items);
        }

        const objectValues = Object.values(serviceDataLike);
        if (objectValues.length && objectValues.every((val) => Array.isArray(val))) {
            const maxLength = Math.max(...objectValues.map((val) => val.length));
            const combined = [];
            for (let index = 0; index < maxLength; index += 1) {
                const entry = {};
                Object.entries(serviceDataLike).forEach(([key, val]) => {
                    if (Array.isArray(val)) {
                        entry[key] = val[index];
                    }
                });
                combined.push(entry);
            }
            return combined;
        }

        if (objectValues.length && objectValues.every((val) => typeof val === 'object' && val !== null)) {
            return objectValues.flatMap((val) => parseServiceDataFlexible(val));
        }

        return [];
    }
    return [];
};

const createNormalizedServiceEntry = (entry, fallbackName = '', fallbackPrice = 0) => {
    if (!entry) return null;
    const resolvedName =
        entry.name ??
        entry.serviceName ??
        entry.service_name ??
        entry.title ??
        entry.service_title ??
        fallbackName ??
        '';
    const resolvedPrice =
        entry.price ??
        entry.servicePrice ??
        entry.service_price ??
        entry.amount ??
        entry.total ??
        fallbackPrice ??
        0;

    const numericPrice = coerceNumberValue(resolvedPrice);
    const formattedPrice = numericPrice.toFixed(2);

    return {
        ...entry,
        name: resolvedName,
        serviceName: resolvedName,
        service_name: resolvedName,
        title: entry.title ?? resolvedName,
        price: numericPrice,
        servicePrice: formattedPrice,
        service_price: formattedPrice,
    };
};

const buildEntriesFromNamedValues = (record) => {
    const nameSources = [
        record?.serviceName,
        record?.serviceNames,
        record?.service_name,
        record?.service_title,
        record?.title,
    ];
    const priceSources = [
        record?.servicePrice,
        record?.servicePrices,
        record?.service_price,
        record?.service_price_total,
        record?.price,
        record?.amount,
        record?.total,
        record?.paid_amount,
        record?.payment_amount,
    ];

    let names = [];
    let prices = [];

    for (const candidate of nameSources) {
        if (Array.isArray(candidate) && candidate.length) {
            names = candidate.filter(Boolean);
            break;
        }
    }
    if (!names.length) {
        const stringCandidate = nameSources.find((candidate) => typeof candidate === 'string' && candidate.trim());
        if (stringCandidate) {
            names = [stringCandidate.trim()];
        }
    }

    for (const candidate of priceSources) {
        if (Array.isArray(candidate) && candidate.length) {
            prices = candidate;
            break;
        }
    }
    if (!prices.length) {
        const scalarCandidate = priceSources.find(
            (candidate) => candidate !== undefined && candidate !== null && candidate !== '',
        );
        if (scalarCandidate !== undefined) {
            prices = [scalarCandidate];
        }
    }

    if (!names.length && !prices.length) {
        return [];
    }

    const length = Math.max(names.length, prices.length, 1);
    const entries = [];

    for (let index = 0; index < length; index += 1) {
        const name = names[index] ?? names[0] ?? '';
        const price = prices[index] ?? prices[0] ?? 0;
        entries.push(
            createNormalizedServiceEntry(
                {
                    name,
                    serviceName: name,
                    service_name: name,
                    price,
                    servicePrice: price,
                    service_price: price,
                },
                name,
                price,
            ),
        );
    }

    return entries;
};

const buildNormalizedServiceData = (record) => {
    if (!record) return [];

    let entries = parseServiceDataFlexible(record?.service_data);

    if ((!entries || !entries.length) && record?.serviceData) {
        entries = parseServiceDataFlexible(record.serviceData);
    }

    if ((!entries || !entries.length) && record?._originalData) {
        const original = record._originalData;
        entries =
            parseServiceDataFlexible(original?.service_data) || parseServiceDataFlexible(original?.serviceData);
    }

    let normalized = (entries || []).map((entry) => createNormalizedServiceEntry(entry)).filter(Boolean);

    if (!normalized.length) {
        normalized = buildEntriesFromNamedValues(record);
    }

    if (!normalized.length && record?._originalData) {
        normalized = buildEntriesFromNamedValues(record._originalData);
    }

    return normalized.filter(Boolean);
};

const PurchasedServicePaymentPopup = ({ open, onClose, purchasedServiceData }) => {
    const { token } = useAuth();
    const [business, setBusiness] = useRecoilState(businessDataAtom);
    const [formData, setFormData] = useState(null);
    const [providers, setProviders] = useState([]);
    const [servicesList, setServicesList] = useState([]);
    const [servicesCount, setServicesCount] = useState(1);
    const [serviceStatus, setServiceStatus] = useState('');

    useEffect(() => {
        if (!purchasedServiceData) return;

        const processPurchasedService = async () => {
            const normalizeDateForMatching = (dateStr) => {
                if (!dateStr) return '';
                try {
                    const date = new Date(dateStr);
                    return date.toISOString().split('T')[0];
                } catch (e) {
                    return dateStr;
                }
            };

            const patientName = purchasedServiceData?.patients?.name ||
                `${purchasedServiceData?.patients?.first_name || ''} ${purchasedServiceData?.patients?.last_name || ''}`.trim() || '';

            let serviceData = buildNormalizedServiceData(purchasedServiceData);

            if (!serviceData || serviceData.length === 0) {
                try {
                    let invoices = null;
                    
                    if (!token) {
                        console.error('No authentication token found');
                    } else {
                        const response = await fetch(
                            `${process.env.REACT_APP_SERVER_URL}/api/providers/invoices?purchased_service_id=${purchasedServiceData.id}&isDeleted=eq.false&limit=1`,
                            {
                                method: 'GET',
                                headers: {
                                    'Content-Type': 'application/json',
                                    'Authorization': `Bearer ${token}`
                                }
                            }
                        );

                        const result = await response.json();

                        if (!response.ok || result.error) {
                            console.error('Error fetching invoices:', result.error || 'Failed to fetch invoices');
                        } else {
                            // Backend returns { success: true, data: [] } or array directly
                            invoices = result.success && result.data !== undefined ? result.data : (Array.isArray(result) ? result : null);
                        }
                    }

                    if (!invoices || invoices.length === 0) {
                        const psDate = normalizeDateForMatching(purchasedServiceData.purchase_date || purchasedServiceData.paid_date || purchasedServiceData.date_of_service || purchasedServiceData.created_at);
                        
                        if (!token) {
                            console.error('No authentication token found');
                        } else {
                            try {
                                const response = await fetch(
                                    `${process.env.REACT_APP_SERVER_URL}/api/providers/invoices?patient_id=${purchasedServiceData.patient_id}&isDeleted=eq.false&limit=100`,
                                    {
                                        method: 'GET',
                                        headers: {
                                            'Content-Type': 'application/json',
                                            'Authorization': `Bearer ${token}`
                                        }
                                    }
                                );

                                const result = await response.json();

                                if (!response.ok || result.error) {
                                    console.error('Error fetching all invoices:', result.error || 'Failed to fetch invoices');
                                } else {
                                    // Backend returns { success: true, data: [] } or array directly
                                    const allInvoices = result.success && result.data !== undefined ? result.data : (Array.isArray(result) ? result : []);

                                    if (allInvoices && allInvoices.length > 0) {
                                        invoices = allInvoices.filter(inv => {
                                            const invDate = normalizeDateForMatching(inv.date_of_service || inv.created_at);
                                            return invDate === psDate;
                                        });
                                    }
                                }
                            } catch (error) {
                                console.error('Error fetching all invoices:', error);
                            }
                        }
                    }

                    if (invoices && invoices.length > 0 && invoices[0].service_data) {
                        serviceData = buildNormalizedServiceData(invoices[0]);
                    }
                } catch (error) {
                    console.error('Error fetching linked invoice:', error);
                }
            }

            const { serviceName, servicePrice } = serviceData.reduce(
                (acc, curr) => {
                    const name = curr?.name || curr?.serviceName || curr?.service_name || curr?.title || '';
                    const price = coerceNumberValue(curr?.price ?? curr?.servicePrice ?? curr?.service_price);
                    acc.serviceName.push(name || '');
                    acc.servicePrice.push(price > 0 ? price.toFixed(2) : '0.00');
                    return acc;
                },
                { serviceName: [], servicePrice: [] },
            );

            const count = serviceName.length > 0 ? serviceName.length : 1;
            setServicesCount(count);

            if (serviceName.length === 0) {
                serviceName.push('');
                servicePrice.push('0.00');
            }

            const computedTotalFromServices = serviceData && serviceData.length > 0
                ? serviceData.reduce(
                    (sum, curr) => sum + coerceNumberValue(curr?.price ?? curr?.servicePrice ?? curr?.service_price),
                    0,
                )
                : 0;

            const totalFromRecord =
                coerceNumberValue(purchasedServiceData?.total) ||
                coerceNumberValue(purchasedServiceData?.paid_amount) ||
                coerceNumberValue(purchasedServiceData?.payment_amount);
            const normalizedTotal = computedTotalFromServices > 0 ? computedTotalFromServices : totalFromRecord;

            const patientEmail = purchasedServiceData?.patients?.email || '';
            const mappedFormData = {
                patientName: patientName,
                patient_id: purchasedServiceData?.patient_id || purchasedServiceData?.patients?.id || null,
                email: patientEmail,
                patientEmail: patientEmail,
                sendInvoiceByInfo: patientEmail, // Map email to sendInvoiceByInfo for display
                phone: purchasedServiceData?.patients?.phone || '',
                dateOfService: purchasedServiceData?.purchase_date ||
                              purchasedServiceData?.date_of_service ||
                              purchasedServiceData?.paid_date ||
                              purchasedServiceData?.created_at || '',
                timeOfService: purchasedServiceData?.time_of_service || '',
                provider: purchasedServiceData?.provider_id || purchasedServiceData?.provider?.id || null,
                serviceName: serviceName,
                servicePrice: servicePrice,
                service_data: serviceData,
                total: normalizedTotal,
                payment_status: purchasedServiceData?.payment_status || purchasedServiceData?.status || 'paid',
            };

            setFormData(mappedFormData);

            let finalStatus = 'paid';
            let linkedInvoice = null;
            try {
                let invoices = null;
                
                if (!token) {
                    console.error('No authentication token found');
                } else {
                    const response = await fetch(
                        `${process.env.REACT_APP_SERVER_URL}/api/providers/invoices?purchased_service_id=${purchasedServiceData.id}&isDeleted=eq.false&limit=1`,
                        {
                            method: 'GET',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`
                            }
                        }
                    );

                    const result = await response.json();

                    if (!response.ok || result.error) {
                        console.error('Error fetching invoices:', result.error || 'Failed to fetch invoices');
                    } else {
                        // Backend returns { success: true, data: [] } or array directly
                        invoices = result.success && result.data !== undefined ? result.data : (Array.isArray(result) ? result : null);
                    }
                }

                if (!invoices || invoices.length === 0) {
                    const psDate = normalizeDateForMatching(purchasedServiceData.purchase_date || purchasedServiceData.paid_date || purchasedServiceData.date_of_service || purchasedServiceData.created_at);
                    
                    if (!token) {
                        console.error('No authentication token found');
                    } else {
                        try {
                            const response = await fetch(
                                `${process.env.REACT_APP_SERVER_URL}/api/providers/invoices?patient_id=${purchasedServiceData.patient_id}&isDeleted=eq.false&limit=100`,
                                {
                                    method: 'GET',
                                    headers: {
                                        'Content-Type': 'application/json',
                                        'Authorization': `Bearer ${token}`
                                    }
                                }
                            );

                            const result = await response.json();

                            if (!response.ok || result.error) {
                                console.error('Error fetching all invoices:', result.error || 'Failed to fetch invoices');
                            } else {
                                // Backend returns { success: true, data: [] } or array directly
                                const allInvoices = result.success && result.data !== undefined ? result.data : (Array.isArray(result) ? result : []);

                                if (allInvoices && allInvoices.length > 0) {
                                    invoices = allInvoices.filter(inv => {
                                        const invDate = normalizeDateForMatching(inv.date_of_service || inv.created_at);
                                        return invDate === psDate;
                                    });
                                }
                            }
                        } catch (error) {
                            console.error('Error fetching all invoices:', error);
                        }
                    }
                }

                if (invoices && invoices.length > 0) {
                    linkedInvoice = invoices[0];
                }
            } catch (error) {
                console.error('Error fetching linked invoice for status:', error);
            }

            if (linkedInvoice) {
                finalStatus = linkedInvoice.payment_status || linkedInvoice.status || 'paid';
                if (finalStatus?.toLowerCase() !== 'paid' && !linkedInvoice.paid_date) {
                    finalStatus = 'pending';
                } else {
                    finalStatus = 'paid';
                }
            } else {
                const rawStatus = purchasedServiceData.payment_status || purchasedServiceData.status || 'paid';
                if (rawStatus?.toLowerCase() === 'unpaid') {
                    finalStatus = 'pending';
                } else if (rawStatus?.toLowerCase() === 'pending' && !purchasedServiceData.paid_date) {
                    finalStatus = 'pending';
                } else {
                    finalStatus = 'paid';
                }
            }

            setServiceStatus(finalStatus);

            if (serviceData && Array.isArray(serviceData) && serviceData.length > 0) {
                setServicesList(serviceData.map((s) => ({
                    name: s?.name || s?.serviceName || s?.service_name || s?.title || '',
                    price: coerceNumberValue(s?.price ?? s?.servicePrice ?? s?.service_price),
                })));
            }

            if (purchasedServiceData?.provider) {
                setProviders([{
                    id: purchasedServiceData.provider.id,
                    first_name: purchasedServiceData.provider.first_name,
                    last_name: purchasedServiceData.provider.last_name,
                }]);
            }
        };

        processPurchasedService();
    }, [purchasedServiceData]);

    const getTitleText = () => {
        if (!purchasedServiceData) return 'Service Payment';
        return 'Service Payment Details';
    };

    const formatDateDisplay = (dateString) => {
        if (!dateString) return '';
        try {
            const date = new Date(dateString);
            return formatDate(date);
        } catch (e) {
            return dateString;
        }
    };

    const getSubText = () => {
        if (!purchasedServiceData) return '';
        
        // Get patient's first name
        const patientFirstName = purchasedServiceData?.patients?.first_name || 
            purchasedServiceData?.patients?.name?.split(' ')[0] || 
            'Patient';
        
        // Get the purchase date (created_at)
        const purchaseDate = purchasedServiceData?.created_at ? formatDateDisplay(purchasedServiceData.created_at) : '';
        
        if (purchaseDate) {
            return `Purchased by ${patientFirstName} on ${purchaseDate}`;
        }
        
        return '';
    };

    const isContentReady = Boolean(purchasedServiceData && formData);

    const stickyFooter = (
        <StickyActionFooter
            onCancel={onClose}
            cancelText="Close"
        />
    );

    return (
        <FormModal
            open={open}
            onClose={onClose}
            title={getTitleText()}
            subText={getSubText()}
            badge={serviceStatus ? <StatusBadge status={serviceStatus} /> : null}
            stickyFooter={stickyFooter}
        >
            {isContentReady ? (
                <FormStyle>
                    <Grid container spacing={0}>
                        <Grid item xs={12}>
                            <FormHeading>Patient Details</FormHeading>
                        </Grid>

                        <Form
                            formData={formData}
                            setFormData={setFormData}
                            handleChange={() => {}} // No-op for view mode
                            providers={providers}
                            openProviderDropdown={false}
                            setOpenProviderDropdown={() => {}}
                            servicesCount={servicesCount}
                            setServicesCount={() => {}} // No-op for view mode
                            invoice={false}
                            servicesList={servicesList}
                            isViewMode={true}
                            business={business}
                            getProviderServices={() => {}}
                            getBusinessServices={() => {}}
                            purchasedService={true}
                        />
                    </Grid>
                </FormStyle>
            ) : (
                <div className="flex items-center justify-center py-10 text-gray-400 text-sm">
                    Loading service details...
                </div>
            )}
        </FormModal>
    );
};

export default PurchasedServicePaymentPopup;

